/*
 * Pure reverse-funnel projection for the Plan view. Given a goal and the real
 * blended rates (FRACTIONS, 0–1, from /plan-rates), back-solve the funnel up to
 * the spend needed, plus CPA / ROI / P&L / LTV. Null-safe: a missing or zero
 * rate yields nulls downstream rather than NaN/Infinity. This is the only
 * client-side math in the app and is unit-testable in isolation.
 */

export type GoalStage = 'members' | 'bookings';

export interface PlanRates {
  ctr: number | null;
  cpc: number | null;
  lpPct: number | null;
  surPctLumped: number | null;
  ccPct: number | null;
  avgRevPerSale: number | null;
  costPerBooking: number | null;
  lcPct: number | null;
}

export interface PlanConstants {
  membershipPriceNok: number;
  defaultRetentionMonths: number;
}

export interface PlanBenchmarks {
  ctr: number;
  lpPct: number;
  surPct: number;
  ccPct: number;
}

export interface FunnelLevel {
  impressions: number | null;
  clicks: number | null;
  bookings: number | null;
  showedUp: number | null;
  members: number | null;
  spend: number | null;
  revenue: number | null;
}

export interface PlanProjection {
  perDay: FunnelLevel;
  perMonth: FunnelLevel;
  cpa: number | null;
  roi: number | null;
  plPerDay: number | null;
  ltv: number | null;
}

const DAYS = 30;

/** a / b with guards: null if either side is missing or b is ~0. */
function div(a: number | null, b: number | null): number | null {
  if (a === null || b === null || b === 0) return null;
  return a / b;
}
function mul(a: number | null, b: number | null): number | null {
  if (a === null || b === null) return null;
  return a * b;
}

export function projectPlan(
  goalStage: GoalStage,
  goalMonthly: number,
  rates: PlanRates,
  constants: PlanConstants,
): PlanProjection {
  const { ctr, cpc, lpPct, surPctLumped: surPct, ccPct, avgRevPerSale } = rates;

  // Anchor: derive bookings/day from the goal stage.
  let membersDay: number | null;
  let bookingsDay: number | null;
  if (goalStage === 'members') {
    membersDay = goalMonthly / DAYS;
    bookingsDay = div(membersDay, mul(surPct, ccPct));
  } else {
    bookingsDay = goalMonthly / DAYS;
    membersDay = mul(bookingsDay, mul(surPct, ccPct));
  }

  const showedUpDay = mul(bookingsDay, surPct);
  const clicksDay = div(bookingsDay, lpPct);
  const impressionsDay = div(clicksDay, ctr);
  const spendDay = mul(clicksDay, cpc);
  const revPerSale = avgRevPerSale ?? constants.membershipPriceNok;
  const revenueDay = mul(membersDay, revPerSale);

  const perDay: FunnelLevel = {
    impressions: impressionsDay,
    clicks: clicksDay,
    bookings: bookingsDay,
    showedUp: showedUpDay,
    members: membersDay,
    spend: spendDay,
    revenue: revenueDay,
  };

  const perMonth: FunnelLevel = {
    impressions: mul(impressionsDay, DAYS),
    clicks: mul(clicksDay, DAYS),
    bookings: mul(bookingsDay, DAYS),
    showedUp: mul(showedUpDay, DAYS),
    members: mul(membersDay, DAYS),
    spend: mul(spendDay, DAYS),
    revenue: mul(revenueDay, DAYS),
  };

  // Cohort LTV: a month's new members × price × retention months (volume-based,
  // not a single-member figure) — legacy parity.
  const ltv =
    membersDay !== null
      ? membersDay * DAYS * constants.membershipPriceNok * constants.defaultRetentionMonths
      : null;

  return {
    perDay,
    perMonth,
    cpa: div(spendDay, membersDay),
    roi: div(revenueDay, spendDay),
    plPerDay: revenueDay !== null && spendDay !== null ? revenueDay - spendDay : null,
    ltv,
  };
}

export interface ConstraintRow {
  key: keyof PlanBenchmarks;
  label: string;
  current: number | null;
  benchmark: number;
  /** how far below benchmark, as a ratio (0 = at/above benchmark) */
  shortfall: number;
}

/**
 * Rank the funnel rates by how far each sits below its benchmark. The biggest
 * shortfall is the binding constraint — the cheapest lever (improve a rate, no
 * extra spend). Returns rows sorted worst-first.
 */
export function rankConstraints(
  rates: PlanRates,
  benchmarks: PlanBenchmarks,
  labels: Record<keyof PlanBenchmarks, string>,
): ConstraintRow[] {
  const current: Record<keyof PlanBenchmarks, number | null> = {
    ctr: rates.ctr,
    lpPct: rates.lpPct,
    surPct: rates.surPctLumped,
    ccPct: rates.ccPct,
  };
  const keys: (keyof PlanBenchmarks)[] = ['ctr', 'lpPct', 'surPct', 'ccPct'];
  return keys
    .map((key) => {
      const cur = current[key];
      const bench = benchmarks[key];
      const shortfall = cur === null || bench === 0 ? 0 : Math.max(0, (bench - cur) / bench);
      return { key, label: labels[key], current: cur, benchmark: bench, shortfall };
    })
    .sort((a, b) => b.shortfall - a.shortfall);
}
